import { useEffect, useMemo, useState } from 'react';

import {
  AGENT_ACTIVITY_METADATA_KEY,
  AGENT_STATUS_METADATA_KEY,
  DocumentTagID,
  type AgentStatus,
  type ChatMessage,
  type ChatSessionMode,
  type ConversationalFormOption,
} from '@karehero/models';

import { useSelector } from 'store';
import { useListChatSessionsQuery } from 'store/api/chatSession';
import { getCurrentCareCircle } from 'store/slices/careCircleSlice';
import { toChatMessage } from 'utils/chatMessages';
import {
  answersMessage,
  openQuestion,
  shownQuestions,
} from 'utils/serverChatQuestions';
import { useChatSender } from './useChatSender';
import { useChatSessionWriter } from './useChatSessionWriter';
import { useMessageOutbox } from './useMessageOutbox';
import { useServerChatHaptics } from './useServerChatHaptics';

/** Whether a message shows the agent took in what was said: its own, or answers it read from it. */
const isTakenIn = (message?: ChatMessage) =>
  message?.from === 'system' ||
  (message?.content.kind === 'answers' && !!message.content.readFrom);

/** What the agent did in each turn, by the member's message it answered. */
type ActivityByMessage = Record<string, string[]>;

/**
 * The messages with what each turn did after the member's message it
 * answered: as it goes, above the typing that says it's still working, and
 * kept there once it's done.
 */
const withActivity = (
  messages: ChatMessage[],
  activity: ActivityByMessage,
): ChatMessage[] =>
  messages.flatMap((message) => {
    const lines = activity[message.id];
    if (!lines?.length) return [message];
    return [
      message,
      {
        id: `activity-${message.id}`,
        from: 'system',
        content: { kind: 'activity', lines },
        status: 'sent',
      },
    ];
  });

export interface UseServerChatOptions {
  /** The mode the session runs in; without one it waits. */
  mode?: ChatSessionMode;
  sessionId: string;
}

/**
 * useServerChat shows a chat session of a mode the server runs, sends what is
 * typed or said into it, and answers the questions it asks. Nothing is read yet.
 */
export const useServerChat = ({ mode, sessionId }: UseServerChatOptions) => {
  const careCircleId = useSelector(getCurrentCareCircle)?.id;

  // The session is read from its mode's list, which is the cache sent messages land in.
  const queryArgs = useMemo(
    () => ({ careCircleId: careCircleId ?? '', mode: mode ?? '' }),
    [careCircleId, mode],
  );
  const { data: sessions } = useListChatSessionsQuery(queryArgs, {
    skip: !careCircleId || !mode,
    refetchOnMountOrArgChange: true,
  });

  const sessionData = sessions?.find(({ session }) => session.id === sessionId);
  const saved = useMemo<ChatMessage[]>(
    () => (sessionData?.messages ?? []).map((m) => toChatMessage(m)),
    [sessionData?.messages],
  );
  const status = sessionData?.session.metadata?.[AGENT_STATUS_METADATA_KEY];
  const isAgentThinking = status === ('thinking' satisfies AgentStatus);
  const agentActivity =
    sessionData?.session.metadata?.[AGENT_ACTIVITY_METADATA_KEY];
  // The member's message the turn being worked on answers: their latest, not
  // counting answers the agent read from what they said.
  const answeringId = useMemo(
    () =>
      [...saved]
        .reverse()
        .find(
          (m) =>
            m.from === 'user' &&
            !(m.content.kind === 'answers' && m.content.readFrom),
        )?.id,
    [saved],
  );
  // Kept while the conversation is open, so what each turn did stays where it
  // happened; it isn't saved, so it's gone once it's left.
  const [activityByMessage, setActivityByMessage] = useState<ActivityByMessage>(
    {},
  );
  useEffect(() => {
    if (!isAgentThinking || !answeringId || !agentActivity?.length) return;
    setActivityByMessage((kept) => ({ ...kept, [answeringId]: agentActivity }));
  }, [isAgentThinking, answeringId, agentActivity]);

  const shown = useMemo(() => shownQuestions(saved), [saved]);
  // The turn going on shows what it has done as soon as it's said, a render
  // before it's kept.
  const isActive = isAgentThinking && !!answeringId && !!agentActivity?.length;
  const messages = useMemo(
    () =>
      withActivity(
        shown,
        isActive
          ? { ...activityByMessage, [answeringId!]: agentActivity! }
          : activityByMessage,
      ),
    [shown, activityByMessage, isActive, answeringId, agentActivity],
  );
  const question = useMemo(() => openQuestion(saved), [saved]);
  // The agent shows nothing but the profile as a card, once there's nothing left to find out.
  const profileId = useMemo(
    () =>
      [...saved]
        .reverse()
        .find((m) => m.from === 'system' && m.content.kind === 'card')?.id,
    [saved],
  );
  // What is already in the session when it loads is not felt, nor what a turn did.
  useServerChatHaptics({ messages: shown, isReady: !!sessionData });

  const { persistMessage, updateMessage } = useChatSessionWriter({
    careCircleId,
    sessionId: sessionData && sessionId,
    queryArgs,
  });
  const { send: sendMessage, resend: resendMessage } =
    useMessageOutbox(updateMessage);
  const sender = useChatSender({
    careCircleId,
    source: mode ?? '',
    documentTag: DocumentTagID.Conversation,
    context: '',
    // The server reads nothing from these conversations yet.
    canRead: false,
    persistMessage,
    updateMessage,
    sendMessage,
  });

  return {
    careCircleId,
    messages,
    // The server says when it is working on a turn, in the session's metadata.
    isAgentThinking,
    /**
     * Whether it has taken in what was said, in the turn it's working on:
     * thinking, with the latest message its own, or answers it read from what
     * they said. A turn that waits for more does neither.
     */
    isAgentReplying: isAgentThinking && isTakenIn(saved.at(-1)),
    /** What it has done so far in the turn it's working on, a line each, if anything. */
    agentActivity,
    // And when its last turn stopped without answering, for them to try again.
    isAgentFailed: status === ('failed' satisfies AgentStatus),
    // And when it is waiting on something sent being read.
    isAgentReading: status === ('reading' satisfies AgentStatus),
    isSessionReady: !!sessionData,
    // Loaded, and the session isn't among its mode's.
    isSessionMissing: !!sessions && !sessionData,
    /** The question the server is asking, when it is asking one. */
    question,
    /** The profile the conversation ended on, once it has been shown. */
    profileId,
    /** Answers the question being asked with the options picked. */
    answer: (options: ConversationalFormOption[]) => {
      if (question) persistMessage(answersMessage(question, options));
    },
    sendText: sender.sendText,
    sendVoiceNote: sender.sendVoiceNote,
    sendVoiceWords: sender.sendVoiceWords,
    attachVoiceRecording: sender.attachVoiceRecording,
    /** Sends files, read once they are processed. */
    sendFiles: sender.sendFiles,
    resendMessage,
  };
};
